import React from "react";
import "./RechargePromoDialog.scss";
import type {
  RechargePromoDialogProps,
  RechargePromoDialogContentProps,
} from "./RechargePromoDialog.types";
import { Dialog, Button, OptimizedImage } from "../../../Base";
import { useCurrencySymbol } from "../../../../hooks/useUtils";
import { Icon } from "../../../Icon";
import { useT } from "../../../../i18n/hooks";

const RechargePromoDialogContent: React.FC<RechargePromoDialogContentProps> = ({
  hms,
  amount,
  showAmountAsPercent,
  amountPercent,
  percentHeroBoldKey,
  percentHeroSuffixKey,
  percentHeroParams,
  img,
  title,
  description,
  titleParams,
  descriptionParams,
  imgWidth,
  imgHeight,
}) => {
  const t = useT();
  const currSymbol = useCurrencySymbol();
  const showPercent = Boolean(showAmountAsPercent && amountPercent != null);
  const splitPercentHero =
    showPercent && percentHeroBoldKey && percentHeroSuffixKey;

  return (
    <div className="recharge-promo-dialog__content">
      <OptimizedImage
        className="recharge-promo-dialog__img"
        src={img}
        alt="recharge-offer"
        width={imgWidth}
        height={imgHeight}
      />
      {title && (
        <div
          className={`recharge-promo-dialog__title${
            showPercent ? " recharge-promo-dialog__title--percent-soft" : ""
          }`}
        >
          {t(title, titleParams)}
        </div>
      )}
      <div
        className={`recharge-promo-dialog__amount${
          showPercent ? " recharge-promo-dialog__amount--percent" : ""
        }${splitPercentHero ? " recharge-promo-dialog__amount--percent-split" : ""}`}
      >
        {splitPercentHero ? (
          <>
            <span className="recharge-promo-dialog__amount-bold">
              {t(percentHeroBoldKey, {
                percent: amountPercent,
                ...percentHeroParams,
              })}
            </span>
            <span className="recharge-promo-dialog__amount-suffix">
              {t(percentHeroSuffixKey)}
            </span>
          </>
        ) : showPercent ? (
          <>{`${amountPercent}%`}</>
        ) : (
          <>
            +{currSymbol}
            {amount}
          </>
        )}
      </div>
      {description && (
        <div
          className={
            showPercent
              ? "recharge-promo-dialog__description recharge-promo-dialog__description--percent"
              : "recharge-promo-dialog__description"
          }
        >
          {t(description, descriptionParams)}
        </div>
      )}
      <div
        className={`recharge-promo-dialog__time-container${
          hms ? "" : " recharge-promo-dialog__time-container--empty"
        }`}
      >
        {hms && (
          <div className="recharge-promo-dialog__time">
            <Icon name="time" />
            <span>{hms}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export const RechargePromoDialog: React.FC<RechargePromoDialogProps> = ({
  className = "",
  visible,
  hms,
  amount,
  showAmountAsPercent,
  amountPercent,
  percentHeroBoldKey,
  percentHeroSuffixKey,
  percentHeroParams,
  img,
  title,
  description,
  titleParams,
  descriptionParams,
  buttonText,
  imgWidth = "16.25rem",
  imgHeight = "12.84881rem",
  buttonOnClick,
  onClose,
}) => {
  const t = useT();
  return (
    <Dialog
      className={`recharge-promo-dialog ${className}`}
      visible={visible}
      onClose={onClose}
      showCloseButton={true}
      closeButtonPosition="bottom-center"
      content={
        <RechargePromoDialogContent
          hms={hms}
          amount={amount}
          showAmountAsPercent={showAmountAsPercent}
          amountPercent={amountPercent}
          percentHeroBoldKey={percentHeroBoldKey}
          percentHeroSuffixKey={percentHeroSuffixKey}
          percentHeroParams={percentHeroParams}
          img={img}
          title={title}
          description={description}
          titleParams={titleParams}
          descriptionParams={descriptionParams}
          imgWidth={imgWidth}
          imgHeight={imgHeight}
        />
      }
      customActions={
        buttonText && (
          <Button onClick={buttonOnClick} variant="primary" block>
            {t(buttonText)}
          </Button>
        )
      }
    />
  );
};

export default RechargePromoDialog;
